import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu, Home, MessageSquare, Skull, User, LogOut, Settings, Shield, FolderOpen, Award } from 'lucide-react';
import { Badge } from './ui/badge';
import { AnimatePresence, motion } from 'framer-motion';
import { getUnreadMessageCount, getChatUnread } from '../services/api';
import { createPortal } from 'react-dom';
import ChatPanel from './core/ChatPanel';

const Navbar = () => {
  const { currentUser, logout, isOwner, isModerator, isReviewer } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [isMobileView, setIsMobileView] = useState(() => window.matchMedia('(max-width: 767px)').matches);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobileView(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const loadChatUnread = useCallback(async () => {
    try { setChatUnread((await getChatUnread()).total || 0); } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!currentUser || isReviewer) return undefined;
    loadChatUnread();
    const interval = setInterval(() => { if (document.visibilityState === 'visible') loadChatUnread(); }, 20000);
    return () => clearInterval(interval);
  }, [currentUser, isReviewer, loadChatUnread]);

  const ChatUnreadBadge = () => chatUnread > 0 ? (
    <span className="bg-amber-600 text-black font-bold text-[10px] px-1.5 py-0.5 rounded-full leading-none" data-testid="chat-unread-badge">{chatUnread > 99 ? '99+' : chatUnread}</span>
  ) : null;

  const loadUnreadCount = useCallback(async () => {
    try {
      const data = await getUnreadMessageCount();
      setUnreadCount(data.unreadCount);
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    if (currentUser && !isReviewer) {
      loadUnreadCount();
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [currentUser, isReviewer, loadUnreadCount]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleBadge = () => {
    if (isOwner) return <Badge className="ml-2" style={{ backgroundColor: '#8B0000', color: '#fff' }}>Creator</Badge>;
    if (isModerator) return <Badge className="ml-2" style={{ backgroundColor: '#4B0082', color: '#fff' }}>Detective</Badge>;
    return null;
  };

  const NavigationLinks = ({ mobile = false, onLinkClick = () => {} }) => (
    <>
      {/* Chat - dropdown trigger (desktop only, mobile uses sheet link) */}
      {mobile && !isReviewer ? (
        <button
          onClick={() => { onLinkClick(); setTimeout(() => setChatOpen(true), 300); }}
          className="flex items-center gap-2 py-2 text-foreground"
          data-testid="chat-nav-btn-mobile"
        >
          <MessageSquare className="w-4 h-4" />
          <span>Chat</span>
          <ChatUnreadBadge />
        </button>
      ) : null}

      <Link to="/feed" onClick={onLinkClick} className={mobile ? "flex items-center gap-2 py-2" : "text-foreground hover:text-red-600 transition-colors"}>
        {mobile && <Home className="w-4 h-4" />}
        <span>Posts</span>
      </Link>
      {!isReviewer && (
      <Link to="/messages" onClick={onLinkClick} className={mobile ? "flex items-center gap-2 py-2 relative" : "text-foreground hover:text-red-600 transition-colors relative flex items-center gap-1"} data-testid="messages-nav-link">
        {mobile && <MessageSquare className="w-4 h-4" />}
        <span>Messages</span>
        {unreadCount > 0 && (
          <Badge className="bg-red-600 text-white text-xs ml-1 px-1.5 min-w-[20px] h-5 flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </Link>
      )}
      <Link to="/tiers" onClick={onLinkClick} className={mobile ? "flex items-center gap-2 py-2" : "text-foreground hover:text-red-600 transition-colors"}>
        {mobile && <Skull className="w-4 h-4" />}
        <span>Tiers</span>
      </Link>
      <Link to="/ranks" onClick={onLinkClick} className={mobile ? "flex items-center gap-2 py-2" : "text-foreground hover:text-red-600 transition-colors"}>
        {mobile && <Award className="w-4 h-4" />}
        <span>Ranks</span>
      </Link>
      <Link to="/store" onClick={onLinkClick} className={mobile ? "flex items-center gap-2 py-2" : "text-foreground hover:text-red-600 transition-colors"}>
        {mobile && <FolderOpen className="w-4 h-4" />}
        <span>Store</span>
      </Link>
      {isOwner && (
        <Link to="/admin" onClick={onLinkClick} className={mobile ? "flex items-center gap-2 py-2" : "text-foreground hover:text-red-600 transition-colors"}>
          {mobile && <Shield className="w-4 h-4" />}
          <span>Admin</span>
        </Link>
      )}
    </>
  );

  return (
    <nav className="border-b border-red-900/20 bg-black/95 backdrop-blur supports-[backdrop-filter]:bg-black/90 sticky top-0 z-50">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-4">
          <Link to="/" className="font-bold hover:opacity-80 transition-opacity flex items-center" data-testid="navbar-logo">
            <img 
              src="/logo.png" 
              alt="Deadline by Daria" 
              className="h-10 w-auto"
              style={{ mixBlendMode: 'screen' }}
            />
          </Link>
        </div>

        {currentUser ? (
          <>
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              {/* Chat dropdown trigger */}
              {!isReviewer && (
              <div className="relative">
                <button
                  onClick={() => setChatOpen(!chatOpen)}
                  className="text-foreground hover:text-red-600 transition-colors flex items-center gap-1"
                  data-testid="chat-nav-btn"
                >
                  <span>Chat</span>
                  <ChatUnreadBadge />
                </button>
                <ChatPanel isOpen={chatOpen && !isMobileView} onClose={() => setChatOpen(false)} onUnreadChange={loadChatUnread} />
              </div>
              )}

              <NavigationLinks />
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                    <Avatar>
                      <AvatarImage src={currentUser.avatarUrl} alt={currentUser.displayName} />
                      <AvatarFallback>{currentUser.displayName.charAt(0)}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{currentUser.displayName}</p>
                      <p className="text-xs leading-none text-muted-foreground">{currentUser.email}</p>
                      <div className="flex items-center mt-1">
                        {getRoleBadge()}
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Menu */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild className="md:hidden">
                  <Button variant="ghost" size="icon">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left">
                  <div className="flex flex-col space-y-4 mt-8">
                    <NavigationLinks mobile onLinkClick={() => setMobileMenuOpen(false)} />
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/login')}>Login</Button>
            <Button onClick={() => navigate('/signup')}>Sign Up</Button>
          </div>
        )}
      </div>

      {/* Mobile chat panel - rendered via portal to escape nav's backdrop-blur containing block */}
      {chatOpen && isMobileView && !isReviewer && createPortal(
        <div 
          className="md:hidden fixed left-0 right-0 bottom-0 z-[60] flex flex-col bg-black/60"
          style={{ top: '4rem' }}
          onClick={() => setChatOpen(false)}
        >
          <div 
            className="bg-[#121215] border-t border-zinc-800 flex flex-col overflow-hidden"
            style={{ height: 'calc(100dvh - 4rem)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <ChatPanel isOpen={chatOpen} onClose={() => setChatOpen(false)} isMobileOverlay onUnreadChange={loadChatUnread} />
          </div>
        </div>,
        document.body
      )}
    </nav>
  );
};

export default Navbar;
